/**
 * Identity Package P4 — the browser-side guest session.
 *
 * A CONVENIENCE CACHE, never the source of truth. The server decides who
 * someone is: the cached token is re-validated against GET /loyalty/card/{token}
 * on every use, and a rotated or revoked token simply 404s and the cache is
 * dropped. That ordering matters — the localStorage-only "known guest" this
 * replaces was authoritative, which is why it failed on a borrowed phone and
 * survived a guest who was no longer that person.
 *
 * Scoped per venue because the card token itself is venue-scoped (the company is
 * baked into its signature), and one phone number legitimately belongs to
 * different customer rows at different venues.
 */

const KEY = (venueCode) => `pp_guest::${String(venueCode || "").toLowerCase()}`;

/** Persist who we just identified. Silently no-ops if storage is unavailable. */
export function saveGuest(venueCode, { token, customerId, name }) {
  if (!venueCode || !token) return;
  try {
    localStorage.setItem(
      KEY(venueCode),
      JSON.stringify({ token, customerId: customerId || null, name: name || "", at: Date.now() })
    );
  } catch (_) {
    /* private mode / storage full — the flow must still work, just without memory */
  }
}

/** The cached guest for this venue, or null. Never trusted without a server re-check. */
export function loadGuest(venueCode) {
  if (!venueCode) return null;
  try {
    const raw = JSON.parse(localStorage.getItem(KEY(venueCode)) || "null");
    return raw && raw.token ? raw : null;
  } catch (_) {
    return null;
  }
}

/** Forget this guest — "not you?", or a token the server no longer accepts. */
export function clearGuest(venueCode) {
  try {
    localStorage.removeItem(KEY(venueCode));
  } catch (_) {}
}

/** The bookmarkable card URL for a token, on whatever host we're served from. */
export function cardUrl(token) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/c/${encodeURIComponent(token)}`;
}
